/**
 * Helpers for opening external Access products (e.g. Access Financials
 * Operations) from within the embedded Projects app.
 *
 * The Projects app runs as an iframe product inside the Access Evo platform.
 * Navigating the iframe directly (an anchor / window navigation) replaces the
 * Projects app inside its own workspace tab, which is not what we want for a
 * deep-link into another product.
 *
 * Instead we ask the platform to launch the target product in its own tab by
 * posting a `LaunchProduct` message to `window.parent`. The message uses the
 * standard nested `{ payload: { type, data } }` envelope that the workspace
 * shell listens for, and we set `targetOrigin: '*'` so it is delivered across
 * the (different) parent-shell origin.
 *
 * The standalone `launchProduct` helper from `@access-evo/evo-api` posts this
 * same envelope but omits `targetOrigin`, so the browser defaults to
 * same-origin delivery and the cross-origin parent never receives it.
 *
 * With the default launch behaviour the platform reuses an already-open tab for
 * the same product instance, or opens a new one.
 */

/** Workspace template id for Access Financials Operations (FinOps). */
export const FINOPS_PRODUCT_TEMPLATE_ID = 'Access.Product.FinancialsOperations';

/** Workspace template id for Access Time / FocalPoint (TimeTrack Pro). */
export const TIMETRACK_PRODUCT_TEMPLATE_ID = 'access.erp.timetrackpro';

function normalizeInstanceId(instanceId: string | undefined): string {
  return instanceId ? instanceId.replace(/-/gi, '').toLowerCase() : '';
}

/**
 * Requests the parent workspace platform to launch a product in its own tab,
 * deep-linking to `url` when one is known. Products that have no deep link can
 * omit `url` — the platform then opens the product at its default location.
 * Falls back to opening `url` in a new browser tab when the app is not embedded
 * in the workspace (e.g. standalone local dev).
 */
export function launchWorkspaceProduct(
  templateId: string,
  instanceId: string | undefined,
  url?: string,
  title?: string,
): void {
  const cleanInstanceId = normalizeInstanceId(instanceId);
  const embedded =
    typeof window !== 'undefined' && !!window.parent && window.parent !== window;

  if (embedded && cleanInstanceId) {
    try {
      // Post using the nested payload/data format the workspace shell expects.
      // We specify targetOrigin: '*' so the message reaches the cross-origin
      // parent shell (the standalone `launchProduct` from the SDK omits it,
      // which makes the browser default to same-origin delivery).
      window.parent.postMessage(
        {
          payload: {
            type: 'LaunchProduct',
            data: {
              widgetId: templateId,
              instanceId: cleanInstanceId,
              url,
              name: title,
            },
          },
        },
        '*',
      );
      return;
    } catch {
      // fall through to opening in a new browser tab
    }
  }

  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Launches an Access Financials Operations deep link in its own workspace tab. */
export function launchFinOpsProduct(
  instanceId: string | undefined,
  url: string,
  title?: string,
): void {
  launchWorkspaceProduct(FINOPS_PRODUCT_TEMPLATE_ID, instanceId, url, title);
}

/**
 * Launches Access Time / FocalPoint in its own workspace tab. `url` is optional
 * because those products expose no per-entry deep link — when it is omitted the
 * platform opens the product itself.
 */
export function launchTimeTrackProduct(
  instanceId: string | undefined,
  url?: string,
  title?: string,
): void {
  launchWorkspaceProduct(TIMETRACK_PRODUCT_TEMPLATE_ID, instanceId, url, title);
}
